import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Sparkle, WarningCircle } from '@phosphor-icons/react';
import UpgradeDialog from './UpgradeDialog';

// "Upgrade" pill — small, eye-catching button that opens the plan-comparison
// modal. Mirrors Jira's top-bar Upgrade pattern. Renders as null when the
// workspace is already on the top tier (Business) so we never bug paying
// customers with an upsell.

export default function UpgradeButton({ size = 'md', className = '' }) {
  const { status, loading, isExpired } = useSubscription();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;
  // Three visual states:
  //  • Free / trial  → bold purple "Upgrade" pill (drives conversion).
  //  • Business      → quieter "Business" pill (opens same dialog so admins
  //                    can review what's included). Keeps the button always
  //                    visible as a plan indicator.
  //  • Expired       → red "Expired · Renew" pill. Paid window has lapsed;
  //                    effective caps are now Free, so we surface renewal.
  const tier = status?.plan_tier
    || (status?.plan === 'business' || status?.plan === 'active' ? 'business' : 'free');
  const isPaid = tier === 'business' && !isExpired;

  const sz = size === 'sm'
    ? 'h-7 px-2.5 text-[10.5px]'
    : 'h-8 px-3 text-[11.5px]';

  if (isExpired) {
    return (
      <button
        type="button"
        onClick={() => navigate('/billing?renew=1')}
        data-testid="upgrade-button"
        data-plan-tier="expired"
        data-plan-expired="true"
        className={`${sz} ${className} inline-flex items-center gap-1.5 rounded-full font-bold uppercase tracking-[0.10em]
          bg-red-600 text-white hover:bg-red-700
          shadow-[0_4px_12px_rgba(220,38,38,0.35)] hover:shadow-[0_6px_18px_rgba(220,38,38,0.45)]
          hover:-translate-y-px transition-all duration-150`}
        title="Your subscription has expired — renew to restore access"
      >
        <WarningCircle size={12} weight="fill" />
        Expired · Renew
      </button>
    );
  }

  const visualClasses = isPaid
    ? `bg-zinc-100 text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-50 hover:ring-indigo-300`
    : `bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 text-white
       hover:from-indigo-600 hover:via-violet-600 hover:to-fuchsia-600
       shadow-[0_4px_12px_rgba(139,92,246,0.35)] hover:shadow-[0_6px_18px_rgba(139,92,246,0.45)]
       hover:-translate-y-px`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="upgrade-button"
        data-plan-tier={tier}
        className={`${sz} ${className} inline-flex items-center gap-1.5 rounded-full font-bold uppercase tracking-[0.10em]
          ${visualClasses} transition-all duration-150`}
        title={isPaid ? 'You\u2019re on the Business plan' : 'See what you get on the Business plan'}
      >
        <Sparkle size={12} weight="fill" />
        {isPaid ? 'Business' : 'Upgrade'}
      </button>
      <UpgradeDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
